import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  ntGetNaspTracker,
  ntPollNaspTracker,
  ntGetNaspTrackerById,
  ntGetNaspTrackerDropdowns,
  ntUpdateNaspTracker,
  ntAddNaspTrackerComment,
  ntExportNaspTracker,
} from '../actions/naspTrackerThunks';
import './NaspTracker.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

const formatDate = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

const formatDateTime = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

const toDateInput = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch { return ''; }
};

const searchMatch = (row, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  return [
    row.id, row.insertUser, row.change, row.jvId, row.tech, row.csr,
    row.siteName, row.aOrP, row.vendor, row.avail, row.case, row.queue,
    row.keyCities, row.polygon, row.vendorId, row.locked, row.more, row.coosCount,
    row.type, row.owner, row.status,
  ].some((v) => v != null && String(v).toLowerCase().includes(s));
};

// ─── ICON: GEAR ───────────────────────────────────────────────────────────────
const IconGear = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

// ─── ICON: DOWNLOAD ───────────────────────────────────────────────────────────
const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

// ─── EDITABLE FIELDS ──────────────────────────────────────────────────────────
const EDITABLE_FIELDS = [
  { key: 'jvId',      label: 'JV Id',      type: 'text'   },
  { key: 'tech',      label: 'Tech',       type: 'text'   },
  { key: 'csr',       label: 'CSR',        type: 'text'   },
  { key: 'siteName',  label: 'Site Name',  type: 'text'   },
  { key: 'aOrP',      label: 'A/P',        type: 'text'   },
  { key: 'vendor',    label: 'Vendor',     type: 'text'   },
  { key: 'avail',     label: 'Avail',      type: 'text'   },
  { key: 'case',      label: 'Case',       type: 'text'   },
  { key: 'queue',     label: 'Queue',      type: 'text'   },
  { key: 'keyCities', label: 'Key Cities', type: 'text'   },
  { key: 'dateOos',   label: 'Date OOS',   type: 'date'   },
  { key: 'daysOos',   label: 'Days OOS',   type: 'number' },
  { key: 'polygon',   label: 'Polygon',    type: 'text'   },
  { key: 'vendorId',  label: 'Vendor Id',  type: 'text'   },
  { key: 'eta',       label: 'ETA',        type: 'date'   },
  { key: 'locked',    label: 'Locked',     type: 'text'   },
  { key: 'more',      label: 'More',       type: 'text'   },
  { key: 'coosCount', label: 'COOs',       type: 'text'   },
  { key: 'type',      label: 'Type',       type: 'select' },
  { key: 'owner',     label: 'Owner',      type: 'select' },
  { key: 'status',    label: 'Status',     type: 'select' },
];

// ─── EDIT MODAL ───────────────────────────────────────────────────────────────
function EditModal({ row, comments, dropdowns, loadingDropdowns, loading, loadingComment, onClose, onSave, onAddComment, userId }) {
  const [form,        setForm]        = useState(() => {
    const init = { ...row };
    EDITABLE_FIELDS.forEach((f) => {
      if (f.type === 'date') init[f.key] = toDateInput(row[f.key]);
    });
    return init;
  });
  const [newComment, setNewComment] = useState('');
  const commentsEndRef              = useRef(null);

  // scroll comments to bottom on open/update
  useEffect(() => {
    if (commentsEndRef.current)
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    onAddComment({ tableId: row.id, comment: newComment.trim() }, () => setNewComment(''));
  };

  const renderField = (f) => {
    if (f.type === 'select') {
      const options = dropdowns?.[f.key] ?? [];
      return (
        <select
          className="nt-edit-input"
          value={form[f.key] ?? ''}
          onChange={(e) => handleChange(f.key, e.target.value)}
          disabled={loadingDropdowns}
        >
          <option value="">{loadingDropdowns ? 'Loading…' : '—'}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.text}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        className="nt-edit-input"
        type={f.type}
        value={form[f.key] ?? ''}
        onChange={(e) => handleChange(f.key, e.target.value)}
      />
    );
  };

  return createPortal(
    <div className="nt-modal-overlay">
      <div className="nt-modal">
        <div className="nt-modal-header">
          <span className="nt-modal-title">Edit</span>
          <button className="nt-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="nt-modal-body">
          {/* ── Fields grid ── */}
          <div className="nt-edit-grid">
            {EDITABLE_FIELDS.map((f) => (
              <div key={f.key} className="nt-edit-field">
                <label className="nt-edit-label">{f.label}</label>
                {renderField(f)}
              </div>
            ))}
          </div>

          {/* ── Comments ── */}
          <div className="nt-comments-section">
            <span className="nt-comments-title">Comments</span>

            {/* List */}
            <div className="nt-comments-list">
              {comments.length === 0 ? (
                <p className="nt-comments-empty">No comments yet.</p>
              ) : comments.map((c) => (
                <div key={c.id} className="nt-comment-item">
                  <div className="nt-comment-meta">
                    <span className="nt-comment-date">{formatDateTime(c.insertDate)}</span>
                    <span className="nt-comment-user">{c.insertUser}</span>
                  </div>
                  <span className="nt-comment-text">{c.comment}</span>
                </div>
              ))}
              <div ref={commentsEndRef} />
            </div>

            {/* New comment input */}
            <div className="nt-comment-new">
              <input
                className="nt-edit-input"
                type="text"
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                disabled={loadingComment}
              />
              <button
                className="nt-btn nt-btn-primary"
                onClick={handleAddComment}
                disabled={loadingComment || !newComment.trim()}
                style={{ flexShrink: 0 }}
              >
                {loadingComment ? <span className="nt-spinner" style={{ width: 12, height: 12 }} /> : null}
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="nt-modal-footer">
          <button className="nt-btn nt-btn-ghost" onClick={onClose} disabled={loading}>Close</button>
          <button className="nt-btn nt-btn-primary" onClick={() => onSave(form)} disabled={loading}>
            {loading ? <span className="nt-spinner" style={{ width: 12, height: 12 }} /> : null}
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── SORT ICON ────────────────────────────────────────────────────────────────
const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}>
      <polyline points="6 9 12 3 18 9"/><polyline points="6 15 12 21 18 15"/>
    </svg>
  );
  return sortDir === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}><polyline points="6 9 12 3 18 9"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}><polyline points="6 15 12 21 18 15"/></svg>;
};

// ─── TABLE ────────────────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'jvId',     label: 'JV Id'     },
  { key: 'siteName', label: 'Site Name' },
  { key: 'vendor',   label: 'Vendor'    },
  { key: 'case',     label: 'Case'      },
  { key: 'queue',    label: 'Queue'     },
  { key: 'owner',    label: 'Owner'     },
  { key: 'status',   label: 'Status'    },
];

function NaspTrackerTable({ rows, loading, loadingUpdate, loadingComment, naspTrackerById, loadingById, dropdowns, loadingDropdowns, onGearClick, onSave, onAddComment, onCloseModal, userId }) {
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => { setPage(1); }, [rows.length, search]);

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const filtered = rows.filter((r) => searchMatch(r, search));

  const sorted = sortCol
    ? [...filtered].sort((a, b) => {
        const va = a[sortCol] ?? '';
        const vb = b[sortCol] ?? '';
        const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged      = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const colSpan    = 10;

  const modalRow      = naspTrackerById?.naspTracker      ?? null;
  const modalComments = naspTrackerById?.naspTrackerComments ?? [];

  const handleSave = (form) => onSave(form, onCloseModal);

  return (
    <div>
      {modalRow && (
        <EditModal
          row={modalRow}
          comments={modalComments}
          dropdowns={dropdowns}
          loadingDropdowns={loadingDropdowns}
          loading={loadingUpdate}
          loadingComment={loadingComment}
          onClose={onCloseModal}
          onSave={handleSave}
          onAddComment={onAddComment}
          userId={userId}
        />
      )}

      <div className="nt-search-bar">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className="nt-search-input"
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="nt-search-clear" onClick={() => setSearch('')}>✕</button>
        )}
      </div>

      <div className="nt-table-wrapper">
        <table className="nt-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className="nt-th-sortable" onClick={() => handleSort(col.key)}>
                  {col.label}
                  <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
              <th style={{ textAlign: 'center', width: 48 }}>Options</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="nt-table-empty">
                  <span className="nt-spinner" style={{ marginRight: 8 }} />
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="nt-table-empty">No data available</td>
              </tr>
            ) : paged.map((row) => (
              <tr key={row.id}>
                <td>{row.jvId}</td>
                <td>{row.siteName}</td>
                <td>{row.vendor}</td>
                <td>{row.case}</td>
                <td>{row.queue}</td>
                <td>{row.owner}</td>
                <td>{row.status}</td>
                <td style={{ textAlign: 'center' }}>
                  <button className="nt-gear-btn" onClick={() => onGearClick(row.id)} title="Edit">
                    <IconGear />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && filtered.length > PAGE_SIZE && (
        <div className="nt-pagination">
          <button className="nt-page-btn" onClick={() => setPage(1)}            disabled={page === 1}>«</button>
          <button className="nt-page-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
          <span className="nt-page-info">{page} / {totalPages} <span className="nt-page-total">({filtered.length})</span></span>
          <button className="nt-page-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
          <button className="nt-page-btn" onClick={() => setPage(totalPages)}   disabled={page === totalPages}>»</button>
        </div>
      )}
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function NaspTracker() {
  const classes             = useStyles();
  const dispatch            = useDispatch();
  const loading             = useSelector((state) => state.naspTrackerReducer.loading);
  const naspTracker         = useSelector((state) => state.naspTrackerReducer.naspTracker);
  const naspTrackerById     = useSelector((state) => state.naspTrackerReducer.naspTrackerById);
  const naspTrackerDropdowns = useSelector((state) => state.naspTrackerReducer.naspTrackerDropdowns);
  const userId              = useSelector((state) => state.session.user?.id);

  const [userFlag, setUserFlag] = useState('All');
  const userFlagRef             = useRef('All');
  const pollingRef              = useRef(null);

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  const startPolling = () => {
    pollingRef.current = setInterval(() => {
      dispatch(ntPollNaspTracker(userFlagRef.current));
    }, 5000);
  };

  useEffect(() => {
    dispatch(ntGetNaspTracker(userFlag));
    dispatch(ntGetNaspTrackerDropdowns());
    stopPolling();
    startPolling();
    return () => stopPolling();
  }, []);

  const handleToggle = (flag) => {
    userFlagRef.current = flag;
    setUserFlag(flag);
    stopPolling();
    dispatch(ntGetNaspTracker(flag));
    startPolling();
  };

  const handleGearClick = (id) => {
    dispatch(ntGetNaspTrackerById(id));
  };

  const handleCloseModal = () => {
    dispatch({ type: 'NT_GET_NASP_TRACKER_BY_ID_SUCCESS', payload: null });
  };

  const handleSave = (form, onClose) => {
    dispatch(ntUpdateNaspTracker(form, () => {
      dispatch(ntGetNaspTracker(userFlagRef.current));
      if (onClose) onClose();
    }));
  };

  const handleAddComment = (dto, onSuccess) => {
    dispatch(ntAddNaspTrackerComment({ ...dto, insertUser: userId }, onSuccess));
  };

  const handleExport = () => {
    dispatch(ntExportNaspTracker(userId));
  };

  return (
    <Page className={classes.root} title="NASP Tracker">
      <Header module="NASP" view="NASP Tracker" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="nt-root">
            <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── NASP Tracker ───────────────────────────────────────────── */}
              <div className="nt-card">
                <div className="nt-card-header">
                  <div className="nt-flag-toggle">
                    <button
                      className={`nt-flag-btn${userFlag === 'All' ? ' nt-flag-btn--active' : ''}`}
                      onClick={() => handleToggle('All')}
                    >
                      All
                    </button>
                    <button
                      className={`nt-flag-btn${userFlag === userId ? ' nt-flag-btn--active' : ''}`}
                      onClick={() => handleToggle(userId)}
                    >
                      Mine
                    </button>
                  </div>
                  <button
                    className="nt-btn nt-btn-export"
                    onClick={handleExport}
                    disabled={loading.exportNaspTracker}
                    title="Export"
                  >
                    {loading.exportNaspTracker
                      ? <span className="nt-spinner" style={{ width: 12, height: 12 }} />
                      : <IconDownload />}
                    Export
                  </button>
                </div>
                <div style={{ padding: 0 }}>
                  <NaspTrackerTable
                    rows={naspTracker}
                    loading={loading.naspTracker}
                    loadingById={loading.naspTrackerById}
                    loadingUpdate={loading.updateNaspTracker}
                    loadingComment={loading.addNaspTrackerComment}
                    naspTrackerById={naspTrackerById}
                    dropdowns={naspTrackerDropdowns}
                    loadingDropdowns={loading.naspTrackerDropdowns}
                    onGearClick={handleGearClick}
                    onSave={handleSave}
                    onAddComment={handleAddComment}
                    onCloseModal={handleCloseModal}
                    userId={userId}
                  />
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default NaspTracker;